import { useEffect, useState } from "react";
import { Calculator, Printer, Car, PencilLine } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";

const ACQ = 6000;
const EXT = 2000;
const regFees = (p) => p * 0.0625 + 499 + 135 + 35;
const APR = 21;
const DOWNS = [0, 10, 20, 30];
const TERMS = [36, 48, 60, 72];

const pmt = (price, downPct, n) => {
  const fin = price * (1 - downPct / 100) + ACQ + EXT + regFees(price);
  const r = APR / 100 / 12;
  if (fin <= 0) return 0;
  return (fin * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
};

const usd = (v) => `US$ ${Math.round(v).toLocaleString("en-US")}`;

export default function FinancingSimPage() {
  const [vehicles, setVehicles] = useState([]);
  const [mode, setMode] = useState("stock");
  const [vehicleId, setVehicleId] = useState("");
  const [price, setPrice] = useState("");
  const [customer, setCustomer] = useState("");
  const [miles, setMiles] = useState("");
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    api.get("/financing/vehicle-options").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  const pickVehicle = (id) => {
    setVehicleId(id);
    const v = vehicles.find((x) => x.id === id);
    if (v) {
      setPrice(String(Math.round(v.sale_price || 0) || ""));
      setMiles(v.mileage > 0 ? String(Math.round(v.mileage)) : "");
    }
  };

  const vehicle = vehicles.find((x) => x.id === vehicleId);
  const p = parseFloat(price) || 0;

  const printQuote = async () => {
    if (!p) { toast.error("Escolha um carro ou digite o valor primeiro"); return; }
    setPrinting(true);
    try {
      const res = await api.post("/financing/quote.pdf", {
        vehicle_id: mode === "stock" ? vehicleId : "",
        price: p,
        mileage: parseFloat(miles) || 0,
        customer_name: customer.trim(),
      }, { responseType: "blob" });
      openPdfBlob(res.data, "simulacao-financiamento.pdf");
    } catch {
      toast.error("Erro ao gerar a simulação em PDF");
    } finally { setPrinting(false); }
  };

  return (
    <div data-testid="financing-tab">
      <div className="mb-6">
        <h1 className="font-display font-black text-2xl lg:text-3xl uppercase tracking-tight flex items-center gap-2.5">
          <Calculator className="text-[#C9A227]" size={26} /> Financiamento
        </h1>
        <p className="text-text-secondary text-xs mt-1">Simule a parcela com o cliente e imprima na hora</p>
      </div>

      {/* Carro ou valor manual */}
      <div className="border border-border bg-surface p-4 lg:p-5 mb-4">
        <div className="flex gap-2 mb-3">
          <button
            data-testid="financing-mode-stock"
            onClick={() => setMode("stock")}
            className={`px-4 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5 border ${mode === "stock" ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-border text-text-secondary"}`}
          >
            <Car size={13} /> Carro do estoque
          </button>
          <button
            data-testid="financing-mode-manual"
            onClick={() => { setMode("manual"); setVehicleId(""); }}
            className={`px-4 h-10 text-[10px] font-display font-black uppercase tracking-widest inline-flex items-center gap-1.5 border ${mode === "manual" ? "bg-[#C9A227] text-black border-[#C9A227]" : "border-border text-text-secondary"}`}
          >
            <PencilLine size={13} /> Valor manual
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {mode === "stock" && (
            <select
              data-testid="financing-vehicle-select"
              value={vehicleId}
              onChange={(e) => pickVehicle(e.target.value)}
              className="bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm w-full md:col-span-2"
            >
              <option value="">Escolha o carro...</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.year} {v.make} {v.model} — {usd(v.sale_price || 0)}
                </option>
              ))}
            </select>
          )}
          <div className={mode === "manual" ? "md:col-span-1" : ""}>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-text-secondary">Valor do carro (US$)</label>
            <input
              data-testid="financing-price"
              type="number"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="25000"
              className="bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm w-full mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-text-secondary">Milhas (sai no papel)</label>
            <input
              data-testid="financing-miles"
              type="number"
              inputMode="numeric"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
              placeholder="58000"
              className="bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm w-full mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-text-secondary">Cliente (opcional, sai no papel)</label>
            <input
              data-testid="financing-customer"
              type="text"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder="Nome do cliente"
              className="bg-background border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm w-full mt-1"
            />
          </div>
        </div>
        {vehicle && (
          <p data-testid="financing-vehicle-info" className="text-[11px] text-text-secondary mt-2 font-mono">
            VIN {vehicle.vin} {vehicle.mileage ? `· ${Math.round(vehicle.mileage).toLocaleString("en-US")} mi` : ""} {vehicle.color ? `· ${vehicle.color}` : ""}
          </p>
        )}
      </div>

      {/* Tabela de parcelas */}
      <div className="border border-border bg-surface overflow-x-auto mb-4">
        <table data-testid="financing-grid" className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-background border-b border-border">
              <th className="text-left px-4 py-3 text-[10px] font-display font-black uppercase tracking-widest text-text-secondary">Entrada</th>
              {TERMS.map((n) => (
                <th key={n} className="text-center px-3 py-3 text-[10px] font-display font-black uppercase tracking-widest text-text-secondary">{n} meses</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DOWNS.map((dp) => (
              <tr key={dp} className="border-b border-border/50 last:border-0">
                <td className="px-4 py-3.5">
                  <span className="font-display font-black text-base text-[#C9A227]">{dp}%</span>
                  <span className="text-[11px] text-text-secondary ml-2">{p ? usd(p * dp / 100) : "—"}</span>
                </td>
                {TERMS.map((n) => (
                  <td key={n} data-testid={`financing-cell-${dp}-${n}`} className="text-center px-3 py-3.5 font-display font-bold text-emerald-400">
                    {p ? `${usd(pmt(p, dp, n))}/mês` : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="financing-print"
          onClick={printQuote}
          disabled={printing || !p}
          className="bg-[#C9A227] hover:opacity-90 disabled:opacity-40 text-black px-6 h-12 text-[11px] font-display font-black uppercase tracking-widest inline-flex items-center gap-2"
        >
          <Printer size={15} /> {printing ? "Gerando..." : "Imprimir simulação"}
        </button>
        <p className="text-[10px] text-text-secondary">O papel sai com os dados do carro, valor, entrada e parcelas — pronto pra entregar pro cliente</p>
      </div>
    </div>
  );
}
